import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  CheckCircle, ClipboardList, AlertTriangle, ShieldAlert, Truck,
  FileDown, Mail,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import IncidentReportForm from '../IncidentReportForm';
import EmployeeFieldIncidentForm from './EmployeeFieldIncidentForm';
import AgentViolationReportForm from './AgentViolationReportForm';

const API = process.env.REACT_APP_BACKEND_URL;
const LEAD_ROLES = ['supervisor', 'crew_leader', 'manager'];

/**
 * StaffReportsTab — home of every report an on-field employee can file.
 *
 *   Supervisor / Crew Leader / Manager see 4 sub-tabs:
 *     1) Daily Reports          (their submitted checklists)
 *     2) Incident / Roadside    (vehicle incident / roadside form)
 *     3) Employee Field         (professional safety report → HR + Safety)
 *     4) My Violations          (violations they filed)
 *
 *   Site Agents (default staff) see 2 sub-tabs:
 *     1) File Violation         (6-item NECS policy report)
 *     2) My Violations
 */
export default function StaffReportsTab({
  user, myDailyReports, myViolations,
  properties = [], regions = [], vehicles = [],
}) {
  const isLead = LEAD_ROLES.includes(user?.role);

  if (isLead) {
    return (
      <Tabs defaultValue="daily" className="w-full" data-testid="lead-reports-tabs">
        <TabsList className="bg-zinc-900 border border-zinc-700 flex-wrap h-auto">
          <TabsTrigger value="daily" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="lead-reports-tab-daily">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Daily Reports
          </TabsTrigger>
          <TabsTrigger value="incident" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="lead-reports-tab-incident">
            <Truck className="h-3.5 w-3.5 mr-1.5" /> Incident / Roadside
          </TabsTrigger>
          <TabsTrigger value="field" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="lead-reports-tab-field">
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Employee Field Incident
          </TabsTrigger>
          <TabsTrigger value="violations" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="lead-reports-tab-violations">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> My Violations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-3 mt-3">
          <DailyReportsList reports={myDailyReports} />
        </TabsContent>
        <TabsContent value="incident" className="space-y-3 mt-3">
          <IncidentReportForm regions={regions} vehicles={vehicles} />
        </TabsContent>
        <TabsContent value="field" className="space-y-3 mt-3">
          <EmployeeFieldIncidentForm regions={regions} properties={properties} />
          <FieldIncidentHistory />
        </TabsContent>
        <TabsContent value="violations" className="space-y-3 mt-3">
          <ViolationsList violations={myViolations} />
        </TabsContent>
      </Tabs>
    );
  }

  // Site agent view — file a violation using the 6 NECS policy types
  return (
    <Tabs defaultValue="file" className="w-full" data-testid="agent-reports-tabs">
      <TabsList className="bg-zinc-900 border border-zinc-700">
        <TabsTrigger value="file" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="agent-reports-tab-file">
          <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> File Violation
        </TabsTrigger>
        <TabsTrigger value="mine" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black" data-testid="agent-reports-tab-mine">
          <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> My Reports
        </TabsTrigger>
      </TabsList>
      <TabsContent value="file" className="space-y-3 mt-3">
        <AgentViolationReportForm properties={properties} />
      </TabsContent>
      <TabsContent value="mine" className="space-y-3 mt-3">
        <ViolationsList violations={myViolations} />
      </TabsContent>
    </Tabs>
  );
}

function DailyReportsList({ reports }) {
  return (
    <Card className="bg-zinc-900 border-zinc-700 p-6" data-testid="daily-report-history">
      <h3 className="text-lg font-bold text-white mb-4">Daily Reports</h3>
      {reports.length === 0 ? (
        <p className="text-sm text-zinc-400" data-testid="daily-report-empty">No daily reports submitted yet</p>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const completedCount = Object.values(report.checklist || {}).filter(Boolean).length;
            return (
              <div
                key={report.report_id}
                className="border border-zinc-700 rounded-lg p-4"
                data-testid={`daily-report-item-${report.report_id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm text-white font-semibold">{report.property_name}</p>
                    <p className="text-xs text-zinc-400">
                      {report.report_date} • {report.hours_worked} hrs
                    </p>
                  </div>
                  <Badge className="bg-[#FFD60A] text-black" data-testid={`daily-report-checks-${report.report_id}`}>
                    {completedCount} items checked
                  </Badge>
                </div>
                {report.notes && (
                  <p className="text-xs text-zinc-400 mt-2" data-testid={`daily-report-notes-${report.report_id}`}>
                    Notes: {report.notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ViolationsList({ violations }) {
  if (!violations || violations.length === 0) {
    return (
      <Card className="bg-zinc-900 p-8 text-center">
        <CheckCircle className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <p className="text-zinc-400">No violation reports submitted</p>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {violations.map((v) => (
        <Card key={v.violation_id || v.report_id} className="bg-zinc-900 border-zinc-700 p-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Badge className="bg-amber-700">{(v.violation_type || 'violation').replace(/_/g, ' ')}</Badge>
                <Badge
                  className={
                    v.status === 'pending_company_review' ? 'bg-amber-500 text-black' :
                    v.status === 'active' ? 'bg-blue-500' :
                    v.status === 'resolved' ? 'bg-green-500' : 'bg-red-500'
                  }
                >
                  {(v.status || 'pending').replace(/_/g, ' ')}
                </Badge>
              </div>
              <p className="text-white font-medium">
                {v.property_name} - Unit {v.unit_number}
              </p>
              <p className="text-xs text-zinc-400">{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/**
 * FieldIncidentHistory — supervisor / crew-leader / manager view of
 * their submitted Employee Field Incidents. Each row exposes a
 * one-click "Download PDF Packet" (insurance-ready) and an "Email to
 * Carrier" action so HR / Safety can dispatch the record in seconds.
 */
function FieldIncidentHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/employee-field-incidents`, { credentials: 'include' });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (id) => {
    try {
      const r = await fetch(`${API}/api/employee-field-incidents/${id}/packet.pdf`, {
        credentials: 'include',
      });
      if (!r.ok) return toast.error('PDF download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NECS-FieldIncident-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('PDF download failed');
    }
  };

  const emailPacket = async (id) => {
    const recipient = window.prompt('Insurance carrier email:');
    if (!recipient) return;
    const note = window.prompt('Optional note (context for the carrier):', '') || '';
    try {
      const r = await fetch(`${API}/api/employee-field-incidents/${id}/email-packet`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: recipient, note }),
      });
      const d = await r.json();
      if (r.ok) toast.success(d.message || 'Packet emailed');
      else toast.error(d.detail || 'Send failed');
    } catch (_e) {
      toast.error('Send failed');
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="field-incident-history">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white font-bold text-sm">
          My Field Incident Packets ({items.length})
        </h4>
        {loading && <span className="text-[10px] text-zinc-500">Loading…</span>}
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] italic text-zinc-500">
          No field incidents filed yet. Submit the form above to generate an insurance-ready packet.
        </p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map((it) => (
            <div
              key={it.incident_id}
              className="flex items-center justify-between gap-2 p-2 bg-zinc-950 rounded border border-zinc-800"
              data-testid={`field-incident-row-${it.incident_id}`}
            >
              <div className="min-w-0">
                <p className="text-white text-[12px] font-semibold truncate">
                  {(it.incident_type || 'other').replace(/_/g, ' ')} · {it.employee_name}
                </p>
                <p className="text-[10px] text-zinc-400 truncate">
                  {it.city || '—'} · {it.property_name || ''} ·{' '}
                  {it.created_at ? new Date(it.created_at).toLocaleString() : ''}
                </p>
                {(it.last_packet_email_at) && (
                  <p className="text-[10px] text-emerald-500">
                    ✓ emailed {new Date(it.last_packet_email_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Badge className={`text-[10px] ${
                  it.status === 'resolved' ? 'bg-green-700' :
                  it.status === 'in_review' ? 'bg-blue-700' :
                  it.status === 'closed' ? 'bg-zinc-700' : 'bg-red-700'
                } text-white`}>
                  {(it.status || 'open').replace(/_/g, ' ')}
                </Badge>
                <Button
                  variant="outline" size="sm"
                  className="h-7 border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10 text-[10px]"
                  onClick={() => downloadPdf(it.incident_id)}
                  data-testid={`efi-download-pdf-${it.incident_id}`}
                >
                  <FileDown className="h-3 w-3 mr-1" /> PDF
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="h-7 border-red-500 text-red-400 hover:bg-red-950 text-[10px]"
                  onClick={() => emailPacket(it.incident_id)}
                  data-testid={`efi-email-packet-${it.incident_id}`}
                >
                  <Mail className="h-3 w-3 mr-1" /> Email
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
